import { DatePipe, Location } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import dayjs, { Dayjs } from 'dayjs';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { Driver } from 'src/app/shared/models/driver.model';
import { CANADA_PROVINCES, CERTN_COUNTRIES, USA_PROVINCES } from 'src/app/shared/models/provinces';
import { ROUTE_ADD_RESIDENCE_HISTORY, ROUTE_DQF, ROUTE_DQF_UPLOADS, ROUTE_RESIDENCE_HISTORY } from 'src/app/shared/routes';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { RequirementsCompleteActionComponent } from '../requirements-complete-action/requirements-complete-action.component';
import { ResidenceAddress } from '../shared/models/residence-adress.model';

class AddressModel {
  fullAddress: string;
  streetAddress: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
}
export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY'
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};
const REQUIRED_FIELD_MESSAGE = 'You must enter a value';
const MAX_LENGTH_ERROR_MESSAGE = 'Please enter correct value';
const SAVE_RESIDENCE_HISTORY = 'Save Residence history';
const PATTERN_ERROR_MESSAGE = 'Only letters, spaces and hyphens are acceptable';
const MaximumLengthFieldMessage = (value: number) => `Must be less than ${value} characters`;
const REGEX_LETTERS_SPACES_HYPHENS = '^[A-Za-z- ]*$';
const country_US = 'US';
const country_CA = 'CA';
@Component({
  selector: 'app-residence-history',
  templateUrl: './residence-history.component.html',
  styleUrls: ['./residence-history.component.scss'],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    DatePipe
  ],
  standalone: false
})
export class ResidenceHistoryComponent implements OnInit, OnDestroy {
  isContentReady: boolean = false
  loading: boolean = false;
  addressModel: AddressModel;
  canadaProvinces = CANADA_PROVINCES;
  usaProvinces = USA_PROVINCES;
  form: UntypedFormGroup;
  previousAddressForm: UntypedFormGroup;
  lastDateOfMonth = dayjs()
    .endOf('month')
    .valueOf();
  currentDate = new Date(this.lastDateOfMonth);
  maxDate = dayjs(this.currentDate);
  minDate = dayjs(this.currentDate).subtract(30, 'year');
  maxFromDate = this.maxDate;
  minFromDate = this.minDate;
  currentMonthYear = dayjs(this.currentDate).format('MMMM YYYY');
  lastDate = dayjs(this.currentDate).subtract(3, 'year');
  lastMonthYear = dayjs(this.lastDate).format('MMMM YYYY');

  // Month and year options for dropdowns
  months = [
    { value: 0, name: 'January' },
    { value: 1, name: 'February' },
    { value: 2, name: 'March' },
    { value: 3, name: 'April' },
    { value: 4, name: 'May' },
    { value: 5, name: 'June' },
    { value: 6, name: 'July' },
    { value: 7, name: 'August' },
    { value: 8, name: 'September' },
    { value: 9, name: 'October' },
    { value: 10, name: 'November' },
    { value: 11, name: 'December' }
  ];

  // Generate years from 30 years ago to current year (no future years)
  years: number[] = (() => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 30;
    const yearsArray: number[] = [];
    for (let year = currentYear; year >= startYear; year--) {
      yearsArray.push(year);
    }
    return yearsArray;
  })();
  progressBarValue: number = 100;
  addresses: ResidenceAddress[] = [];
  displayAddresses: ResidenceAddress[] = []; // Timeline with gaps filled
  isCurrentAddressEdit: boolean = false;
  isPreviousAddress: boolean = false;
  isLessThan3years: boolean = false;
  isEditAddress: boolean = false;
  isNewCurrentAddressAdd: boolean = false;
  isCurrentAddressNotPresent: boolean = false;
  isAddingCurrentAddress: boolean = false;
  errorMessage: string;
  editFormTitle: string = 'Current Address';
  currentIndex: number = 0;
  turnOffAddressLookup: boolean = false;
  countries: any;
  certnCountries = CERTN_COUNTRIES;
  countryUSorCA: string[] = [country_US, country_CA];
  driver: Driver;
  showAddNewCurrentText: boolean;
  isCurrentAddressValid: boolean = false;
  isGoogleAddressLookupEnabled: boolean = true;
  autocompleteInstances = new Map<string, google.maps.places.Autocomplete>();
  @ViewChild('currentAddress', { read: ViewContainerRef }) currentTemplateForm: ViewContainerRef;
  @ViewChild('previousAddress', { read: ViewContainerRef }) previousTemplateForm: ViewContainerRef;
  @ViewChildren('currentToDatePicker') toDateList;
  @ViewChildren('fromCurrentAddressPicker') fromDateList;
  @ViewChildren('fromPreviousAddressPicker') fromPreviousDateList;
  @ViewChildren('previousAddressToDatePicker') toPreviousDateList;
  @ViewChild('addressInput', { static: false }) addressInput!: ElementRef | undefined;
  private initialized = false;
  private pendingAutocompleteInit: number | null = null; // Track pending initialization with index
  constructor(
    private readonly registrationService: RegistrationService,
    private readonly location: Location,
    private readonly zone: NgZone,
    private readonly fb: UntypedFormBuilder,
    private toastService: ToastService,
    private readonly bottomSheet: MatBottomSheet,
    private readonly router: Router,
    private cdr: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.form = this.createForm();
    this.previousAddressForm = this.createPreviousAddressForm();
    this.loadAllAddresses();
    this.certnCountries = this.certnCountries.map(country => {
      if (!this.countryUSorCA.includes(country.value)) {
        country.value = country.key;
      } else if (country.value === country_US) {
        country.key = 'USA';
      }
      return country;
    }).filter((country, index, self) =>
      index === self.findIndex(c => c.value === country.value)
    );
    this.countries = this.certnCountries;
  }

  ngAfterViewInit(): void {
    if (this.isGoogleAddressLookupEnabled) {
      // Use a longer delay for initial load to ensure Google Maps API is ready
      setTimeout(() => {
        if (this.addressInput?.nativeElement) {
          this.getPlaceAutocomplete(this.addressInput.nativeElement, false);
        } else {
          // Set pending flag if we have a currentIndex to work with
          if (this.isCurrentAddressEdit && this.currentIndex !== undefined) {
            this.pendingAutocompleteInit = this.currentIndex;
          }
        }
      }, 200);
    }
  }

  ngAfterViewChecked(): void {
    if (this.pendingAutocompleteInit !== null && this.isGoogleAddressLookupEnabled && this.isCurrentAddressEdit) {
      // Try to find the input element
      const inputId = '#addressText' + this.pendingAutocompleteInit;
      const inputElement = document.querySelector(inputId) as HTMLInputElement;
      
      if (inputElement) {
        this.getPlaceAutocomplete(inputElement, false);
        this.pendingAutocompleteInit = null; // Clear the pending flag
      }
    }
  }

  ngOnDestroy() {
    sessionStorage.removeItem('orderPccOutsideDqf');
    
    // Clean up all autocomplete instances to prevent memory leaks
    this.autocompleteInstances.forEach((autocomplete, elementId) => {
      const element = document.getElementById(elementId);
      if (element) {
        google.maps.event.clearInstanceListeners(element);
      }
    });
    this.autocompleteInstances.clear();
  }
  goBack(): void {
    const isFromDQF = this.isNavigatedFromDQF();
    if (isFromDQF) {
      // Navigate back to DQF uploads page
      this.router.navigateByUrl(`${ROUTE_DQF}/${ROUTE_DQF_UPLOADS}?type=Driver Uploads`);
    } else {
      this.location.back();
    }
  }

  private isNavigatedFromDQF(): boolean {
    // Check if we came from DQF by looking at the referrer or route history
    const referrer = document.referrer;

    // Check if referrer contains DQF routes
    if (referrer && (referrer.includes('/driverQualification/') || referrer.includes('/dqf/'))) {
      return true;
    }

    // Check if there's a DQF context in session storage or navigation state
    const navigationState = this.router.getCurrentNavigation()?.extras?.state;
    if (navigationState && navigationState['fromDQF']) {
      return true;
    }

    // Check if we have DQF-related query params or state
    if (this.activatedRoute.snapshot.queryParams['fromDQF'] ||
      this.activatedRoute.snapshot.queryParams['dqfId']) {
      return true;
    }

    return false;
  }

  loadAllAddresses(): void {
    this.isContentReady = false;
    this.registrationService.getAllAddress().subscribe(res => {
      this.addresses = res;
      const currentResidence = this.addresses.find(e => e.isCurrentAddress);
      if (this.addresses.length) {
        this.addresses.sort((a, b) => new Date(b.livedFromDate).getTime() - new Date(a.livedFromDate).getTime());
        this.checkMissingGap();
        // if there is no currentResidence in addresses
        if (this.addresses.length > 0 && !currentResidence) {
          this.progressBarValue = 90;
        }
        // for berbix flow - when currentAddress doesnt have fromDate
        this.checkIfCurrentAddressIsUpdate();
      } else {
        this.isLessThan3years = false;
        this.progressBarValue = 100;
        this.isCurrentAddressNotPresent = true;
        // First-time flow: open current address form by default to allow saving it
        this.onAddNewCurrentAddress(true, -1);
      }
      this.registrationService.loadDriver().subscribe(() => {
        this.driver = this.registrationService.getDriver();
        if (this.location.path().includes(ROUTE_DQF)) {
          if (
            !this.driver.residenceHistoryRequirementsCompleted &&
            currentResidence &&
            currentResidence.modifiedDate &&
            dayjs(currentResidence.modifiedDate)
              .set('date', 1)
              .format('YYYY MM') <
            dayjs(this.currentDate)
              .set('date', 1)
              .format('YYYY MM')
          ) {
            this.openRequirementsActionSheet();
            this.progressBarValue = 90;
          } else {
            // after requirements stage user refreshed the page
            if (!this.driver.residenceHistoryRequirementsCompleted && this.addresses.length && !currentResidence) {
              this.showAddNewCurrentText = true;
            }
          }
        }
        const orderPccOutsideofDQF = sessionStorage.getItem('orderPccOutsideDqf');
        if (orderPccOutsideofDQF && this.driver.residenceHistoryRequirementsCompleted) {
          this.router.navigate(['/driverQualification/birthLocation']);
        }
        this.isContentReady = true;
      });
    });
  }

  checkIfCurrentAddressIsUpdate(): void {
    if (this.addresses && this.addresses.length > 0) {
      this.isCurrentAddressNotPresent = false;
      let isCurrentAddress = this.addresses.find(el => el.isCurrentAddress);
      if (isCurrentAddress && this.addresses.length === 1) {
        if (isCurrentAddress.livedFromDate == '' || !isCurrentAddress.hasOwnProperty('livedFromDate')) {
          this.isCurrentAddressEdit = true;
          this.isCurrentAddressValid = false;
          this.populateForm(isCurrentAddress);
          
          // Initialize Google autocomplete for this form
          if (this.isGoogleAddressLookupEnabled) {
            // Find the index of this address
            const addressIndex = this.addresses.findIndex(addr => addr.id === isCurrentAddress.id);
            this.currentIndex = addressIndex;
            this.pendingAutocompleteInit = addressIndex;
            this.cdr.detectChanges();
          }
        } else {
          this.isCurrentAddressValid = true;
        }
      } else {
        this.isCurrentAddressValid = true;
      }
    } else {
      this.isCurrentAddressNotPresent = true;
    }
  }

  openRequirementsActionSheet() {
    let currentAddress: ResidenceAddress = this.addresses.find(emp => emp.isCurrentAddress == true);
    if (!currentAddress) {
      return;
    }
    const bottomSheetRef = this.bottomSheet.open(RequirementsCompleteActionComponent, {
      panelClass: 'signature-panel',
      data: {
        currentAddress: currentAddress,
        isEmploymentHistory: false
      },
      disableClose: true
    });
    bottomSheetRef.afterDismissed().subscribe(data => {
      if (data.type === 'yes') {
        this.isContentReady = false;
        let addressModel = { ...currentAddress };
        let id = addressModel.id;
        delete addressModel.id;
        delete addressModel.modifiedDate;
        delete addressModel.creationDate;
        delete addressModel.version;
        delete addressModel.status;
        delete addressModel.livedToDate;
        this.updateAddress(addressModel, id);
      } else {
        // when user selects toDate
        this.isContentReady = false;
        let addressModel = { ...currentAddress };
        addressModel.livedToDate = dayjs(data.data?.toDate)
          .set('date', 1)
          .format('YYYY-MM-DD');
        addressModel.isCurrentAddress = false;
        let id = addressModel.id;
        delete addressModel.id;
        delete addressModel.creationDate;
        delete addressModel.version;
        delete addressModel.status;
        delete addressModel.modifiedDate;
        this.updateAddress(addressModel, id);
      }
    });
  }

  chosenMonthHandler(normalizedMonth: any, datepicker: MatDatepicker<any>, element?: string): void {
    let date: Dayjs;

    // Handle both Dayjs and native Date objects
    if (normalizedMonth && typeof normalizedMonth.get === 'function') {
      // It's a Dayjs object
      date = dayjs()
        .set('month', normalizedMonth.get('month'))
        .set('year', normalizedMonth.get('year'))
        .set('date', 1);
    } else if (normalizedMonth instanceof Date) {
      // It's a native Date object
      date = dayjs(normalizedMonth).set('date', 1);
    } else {
      // Fallback: try to create a dayjs object from whatever we received
      date = dayjs(normalizedMonth).set('date', 1);
    }

    const formControl = new UntypedFormControl(date);
    if (element) {
      if (element == 'previous') {
        this.previousAddressForm.patchValue({
          previousAddressFromDate: formControl.value
        });
      }
      if (element == 'toDateControl') {
        this.previousAddressForm.patchValue({
          previousAddressToDate: formControl.value
        });
      }

      if (element == 'fromCurrentAddressPicker') {
        this.form.patchValue({
          fromDate: formControl.value
        });
      }

      if (element == 'currentToDatePicker') {
        this.form.patchValue({
          toDate: formControl.value
        });
      }
    }
    datepicker.close();
  }

  // Consolidated form control getter utility
  private getFormControl(formName: 'form' | 'previousAddressForm', controlName: string) {
    const targetForm = formName === 'form' ? this.form : this.previousAddressForm;
    return targetForm?.get(controlName);
  }

  // Current address form controls
  get addressId() { return this.getFormControl('form', 'id'); }
  get streetAddress() { return this.getFormControl('form', 'streetAddress'); }
  get city() { return this.getFormControl('form', 'city'); }
  get state() { return this.getFormControl('form', 'state'); }
  get country() { return this.getFormControl('form', 'country'); }
  get zipCode() { return this.getFormControl('form', 'zipCode'); }
  get toDate() { return this.getFormControl('form', 'toDate'); }
  get fromDate() { return this.getFormControl('form', 'fromDate'); }
  get completeAddress() { return this.getFormControl('form', 'completeAddress'); }

  // Previous address form controls
  get fullAddress() { return this.getFormControl('previousAddressForm', 'fullAddress'); }
  get previousAddress() { return this.getFormControl('previousAddressForm', 'previousAddress'); }
  get previousAddressCity() { return this.getFormControl('previousAddressForm', 'previousAddressCity'); }
  get previousAddressState() { return this.getFormControl('previousAddressForm', 'previousAddressState'); }
  get previousAddressCountry() { return this.getFormControl('previousAddressForm', 'previousAddressCountry'); }
  get previousAddressZipCode() { return this.getFormControl('previousAddressForm', 'previousAddressZipCode'); }
  get previousAddressFromDate() { return this.getFormControl('previousAddressForm', 'previousAddressFromDate'); }
  get previousAddressToDate() { return this.getFormControl('previousAddressForm', 'previousAddressToDate'); }

  get isCurrentAddress() {
    return this.form.get('isCurrentAddress');
  }

  // Helper method to check if TO date fields should be disabled
  get shouldDisableToDate(): boolean {
    const isCurrentAddressValue = this.form.get('isCurrentAddress')?.value;
    return isCurrentAddressValue || this.isAddingCurrentAddress;
  }

  // Helper methods for displaying current date as text
  getCurrentMonthName(): string {
    const currentMonth = dayjs(this.currentDate).month();
    return this.months[currentMonth]?.name || '';
  }

  getCurrentYear(): number {
    return dayjs(this.currentDate).year();
  }

  getStreetAddressErrorMessage(): string {
    return this.streetAddress.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getCityAddressErrorMessage(): string {
    return this.city.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getStateAddressErrorMessage(): string {
    return this.state.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getCountryErrorMessage(): string {
    return this.country.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getZipcodeErrorMessage(): string {
    return this.zipCode.errors.required
      ? REQUIRED_FIELD_MESSAGE
      : this.zipCode.errors.maxLength
        ? MAX_LENGTH_ERROR_MESSAGE
        : '';
  }
  getFromDateErrorMessage(): string {
    return this.fromDate.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getToDateErrorMessage(): string {
    return this.toDate.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getFullAddressErrorMessage(): string {
    return this.fullAddress.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getPreviousAddressErrorMessage(): string {
    return this.previousAddress.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getPreviousAddressCityErrorMessage(): string {
    const errors = this.previousAddressCity.errors;
    return errors.required
      ? REQUIRED_FIELD_MESSAGE
      : errors.pattern
        ? PATTERN_ERROR_MESSAGE
        : errors.maxlength
          ? MaximumLengthFieldMessage(errors.maxlength.requiredLength)
          : '';
  }
  getPreviousAddressStateErrorMessage(): string {
    const errors = this.previousAddressState.errors;
    return errors.required
      ? REQUIRED_FIELD_MESSAGE
      : errors.pattern
        ? PATTERN_ERROR_MESSAGE
        : errors.maxlength
          ? MaximumLengthFieldMessage(errors.maxlength.requiredLength)
          : '';
  }
  getPreviousAddressCountryErrorMessage(): string {
    const errors = this.previousAddressCountry.errors;
    return errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getPreviousAddressZipCodeErrorMessage(): string {
    return this.previousAddressZipCode.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getPreviousAddressFromDate(): string {
    return this.previousAddressFromDate.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getPreviousAddressToDate(): string {
    return this.previousAddressToDate.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }

  addPreviousAddress(index: number): void {
    if (this.isCurrentAddressValid) {
      let toDate = null;
      let fromDate = null;
      this.currentIndex = index;
      this.isPreviousAddress = true;
      this.isCurrentAddressEdit = false;
      this.previousAddressForm = this.createPreviousAddressForm();
      this.form = this.createForm();

      // Check if we're filling a gap from displayAddresses
      const displayItem = this.displayAddresses[index];
      const isFillingGap = displayItem && (displayItem as any).isGap;

      if (isFillingGap && (displayItem as any).expectedFromDate && (displayItem as any).expectedToDate) {
        // Filling a gap - use expected dates to pre-populate and constrain
        const gapFromDate = dayjs((displayItem as any).expectedFromDate);
        const gapToDate = dayjs((displayItem as any).expectedToDate);

        // Pre-populate FROM date with the gap's expected start date
        fromDate = gapFromDate.startOf('month');
        this.previousAddressFromDate.setValue(fromDate);
        
        // Pre-populate TO date with the gap's expected end date (end of month)
        toDate = gapToDate.endOf('month');
        this.previousAddressToDate.setValue(toDate);

        // Set min/max constraints to the gap boundaries
        this.minFromDate = gapFromDate;
        this.maxFromDate = gapToDate;

        // Set the month/year dropdowns
        setTimeout(() => {
          this.previousAddressForm.patchValue({
            previousFromMonth: fromDate.month(),
            previousFromYear: fromDate.year(),
            previousToMonth: toDate.month(),
            previousToYear: toDate.year()
          });
        }, 100);
      } else {
        // Not filling a gap - use original logic for adding addresses beyond existing range
        // Get the earliest address (last in sorted array) to pre-populate TO date
        const earliestAddress = this.addresses[this.addresses.length - 1];

        if (earliestAddress?.livedFromDate) {
          // Pre-populate TO date with the last day of the month before the earliest address starts
          toDate = dayjs(earliestAddress.livedFromDate).subtract(1, 'month').endOf('month');

          this.previousAddressToDate.setValue(toDate);

          // Set the month/year dropdowns with a slight delay to ensure form is ready
          setTimeout(() => {
            this.previousAddressForm.patchValue({
              previousToMonth: toDate.month(),
              previousToYear: toDate.year()
            });
          }, 100);
        }

        // Set date constraints based on existing addresses
        if (index > 0) {
          if (!this.addresses[index]?.livedFromDate) {
            // Check if the next address exists and has livedToDate
            if (this.addresses[index + 1]?.livedToDate) {
              this.minFromDate = dayjs(this.addresses[index + 1].livedToDate);
              this.previousAddressFromDate.setValue(dayjs(this.addresses[index + 1].livedToDate));
            } else {
              this.minFromDate = this.minDate;
            }
            
            // Check if the previous address exists and has livedFromDate
            if (this.addresses[index - 1]?.livedFromDate) {
              this.maxFromDate = dayjs(this.addresses[index - 1].livedFromDate);
            } else {
              this.maxFromDate = this.maxDate;
            }
          } else {
            this.minFromDate = this.minDate;
            this.maxFromDate = this.maxDate;
          }
          
          if (!toDate && this.addresses[index - 1]?.livedFromDate) {
            toDate = dayjs(this.addresses[index - 1].livedFromDate).subtract(1, 'month').endOf('month');
          }
        }
        
        if (index < 0 && !toDate && this.addresses[this.addresses.length - 1]?.livedFromDate) {
          toDate = dayjs(this.addresses[this.addresses.length - 1].livedFromDate).subtract(1, 'month').endOf('month');
        }

        if (toDate && !this.previousAddressToDate.value) {
          this.previousAddressToDate.setValue(toDate);
        }
      }

      // Initialize Google autocomplete after the view updates
      setTimeout(() => {
        const dynamicPreviousAddressId = document.querySelector('#previousAddressText' + index) as HTMLInputElement;
        if (dynamicPreviousAddressId) {
          this.getPlaceAutocomplete(dynamicPreviousAddressId, true);
        }
      });
      this.countries = this.filterCountryDropdown();
    }
  }

  cancelPreviousAddressForm(): void {
    this.resetMaxAndMinValues();
    this.isPreviousAddress = false;
    this.isCurrentAddressEdit = false;
    this.turnOffAddressLookup = false;
    this.resetPreviousAddressValidation();
    this.form = this.createForm();
    this.previousAddressForm = this.createPreviousAddressForm();
  }

  onCancelCurrentAddress(): void {
    this.pendingAutocompleteInit = null; // Clear pending flag
    this.resetMaxAndMinValues();
    this.isCurrentAddressEdit = false;
    this.isPreviousAddress = false;
    this.form = this.createForm();
    this.previousAddressForm = this.createPreviousAddressForm();
  }

  resetMaxAndMinValues(): void {
    this.currentTemplateForm?.clear();
    this.previousTemplateForm?.clear();
    this.minFromDate = this.minDate;
    this.maxFromDate = this.maxDate;
  }

  editIconClicked(address: ResidenceAddress, index: number): void {
    this.currentIndex = index;
    this.isPreviousAddress = false;
    this.isAddingCurrentAddress = false;

    if (!address?.isCurrentAddress) {
      this.editFormTitle = 'Previous Address';
      this.form = this.createForm(false);
    } else {
      this.isAddingCurrentAddress = true;
      this.editFormTitle = 'Current Address';
      this.form = this.createForm(true);
    }

    this.populateForm(address);
    this.isCurrentAddressEdit = true;
    this.cdr.detectChanges();

    if (this.isGoogleAddressLookupEnabled) {
      // Set pending flag - ngAfterViewChecked will complete the initialization when element is available
      this.pendingAutocompleteInit = index;
      
      // Also try immediate initialization in case element is already available
      setTimeout(() => {
        const inputId = '#addressText' + index;
        const inputElement = document.querySelector(inputId) as HTMLInputElement;
        if (inputElement) {
          this.getPlaceAutocomplete(inputElement, false);
          this.pendingAutocompleteInit = null; // Clear the pending flag
        }
      }, 100);
    }
    
    this.countries = this.filterCountryDropdown();
  }

  onAddNewCurrentAddressText() {
    let minFromDate = dayjs(this.addresses[0].livedToDate);
    this.onAddNewCurrentAddress(true, -1, minFromDate);
  }

  onAddNewCurrentAddress(isAddingCurrentAddress: boolean, index: number, minFromDate?: any): void {
    this.currentIndex = index;
    this.editFormTitle = 'Current Address';
    this.form = this.createForm(isAddingCurrentAddress);
    this.previousAddressForm = this.createPreviousAddressForm();
    this.isAddingCurrentAddress = isAddingCurrentAddress;
    this.isCurrentAddressEdit = true;
    this.cdr.detectChanges();

    if (this.isGoogleAddressLookupEnabled) {
      // Set pending flag - ngAfterViewChecked will complete the initialization when element is available
      this.pendingAutocompleteInit = index;
      
      // Also try immediate initialization in case element is already available
      setTimeout(() => {
        const inputId = '#addressText' + index;
        const inputElement = document.querySelector(inputId) as HTMLInputElement;
        if (inputElement) {
          this.getPlaceAutocomplete(inputElement, false);
          this.pendingAutocompleteInit = null; // Clear the pending flag
        }
      }, 100);
    }
    
    const currentAddress = this.addresses.find(address => address.isCurrentAddress === true);

    if (minFromDate) {
      this.minFromDate = minFromDate;
    } else {
      this.minFromDate = currentAddress?.livedFromDate
        ? dayjs(currentAddress.livedFromDate)
        : this.minDate;
    }

    this.maxFromDate = this.maxDate;
    this.countries = this.filterCountryDropdown();
  }

  onCurrentAddressSave(): void {
    // Convert month/year dropdowns to date fields before validation
    this.updateDateFieldsFromMonthYear();

    if (!this.form.valid) {
      return;
    }
    let model = this.prepareSaveModel();
    if (model && model.id) {
      let id = model.id;
      delete model.id;
      if (model.isCurrentAddress) {
        delete model.livedToDate;
      }
      this.updateAddress(model, id);
    } else if (model) {
      if (
        this.isAddingCurrentAddress &&
        !this.showAddNewCurrentText &&
        this.addresses.some(address => address.isCurrentAddress == true)
      ) {
        let currentAddress: ResidenceAddress = { ...this.addresses.find(address => address.isCurrentAddress == true) };
        currentAddress.isCurrentAddress = false;
        currentAddress.livedToDate = dayjs(model.livedFromDate).subtract(1, 'month').endOf('month').format('YYYY-MM-DD');

        const id = currentAddress.id;
        delete currentAddress.id;
        delete currentAddress.status;
        delete currentAddress.modifiedDate;
        delete currentAddress.creationDate;
        delete currentAddress.version;

        this.updateAddress(currentAddress, id, model);
      } else {
        delete model.id;
        model.isCurrentAddress = true;
        delete model.livedToDate;
        this.addNewAddress(model, true);
      }
    }
  }

  onAddPreviousAddress(): void {
    // Convert month/year dropdowns to date fields before validation
    this.updatePreviousDateFieldsFromMonthYear();

    if (!this.previousAddressForm.valid) {
      return;
    }
    let model = this.preparePreviousAddressModel();

    if (model) {
      this.isPreviousAddress = false;
      this.addNewAddress(model);
      this.turnOffAddressLookup = false;
      this.resetPreviousAddressValidation();
      this.form = this.createForm();
      this.previousAddressForm = this.createPreviousAddressForm();
    }
  }
  navigateToPreviousAddressForm(): void {
    this.router.navigateByUrl(`${ROUTE_RESIDENCE_HISTORY}/${ROUTE_ADD_RESIDENCE_HISTORY}`);
  }

  showHideAddButton() {
    if (this.isCurrentAddressEdit || this.isPreviousAddress || this.isLessThan3years) {
      return false;
    }
    return true;
  }

  private addNewAddress(model, updateShowAddNewCurrentText?: boolean): void {
    this.isContentReady = false;
    const orderPccOutsideofDQF = sessionStorage.getItem('orderPccOutsideDqf');
    this.registrationService
      .addAddress(model)
      .pipe(
        catchError((error: any) => {
          this.isContentReady = true;
          this.toastService.showError(error?.message);
          return of(false);
        })
      )
      .subscribe(res => {
        if (res) {
          this.errorMessage = undefined;
          this.toastService.showSuccess('Address added successfully');
          if (updateShowAddNewCurrentText) {
            this.showAddNewCurrentText = false;
          }
          this.isAddingCurrentAddress = false;
          this.loadAllAddresses();
          this.resetFlags();
          if (orderPccOutsideofDQF && this.isLessThan3years) {
            this.router.navigate(['/driverQualification/birthLocation']);
          }
        }
      });
  }
  private updateAddress(model: ResidenceAddress, id: string, newCurrentAddress?: ResidenceAddress): void {
    this.isContentReady = false;
    const orderPccOutsideofDQF = sessionStorage.getItem('orderPccOutsideDqf');
    this.registrationService
      .updateAddress(model, id)
      .pipe(
        catchError((error: any) => {
          this.isContentReady = true;
          this.toastService.showError(error?.message);
          return of(false);
        })
      )
      .subscribe(res => {
        if (res) {
          this.errorMessage = undefined;
          if (this.isAddingCurrentAddress && newCurrentAddress) {
            this.isAddingCurrentAddress = false;
            newCurrentAddress.isCurrentAddress = true;

            delete newCurrentAddress.id;
            delete newCurrentAddress.livedToDate;
            this.addNewAddress(newCurrentAddress);
          } else {
            this.toastService.showSuccess('Address updated successfully');
            this.resetFlags();
            this.loadAllAddresses();
          }
        }
        if (orderPccOutsideofDQF && this.isLessThan3years) {
          this.router.navigate(['/driverQualification/birthLocation']);
        }
      });
  }

  countryLowerCaseCheck(country: string): string {
    return this.countryUSorCA.includes(country) ? country.toLowerCase() : country;
  }
  onDatePickerClick(datepicker: any) {
    datepicker.open();
  }
  private preparePreviousAddressModel(): ResidenceAddress {
    const formModel = this.previousAddressForm.value;
    const addressModel: AddressModel = this.parseAddress(formModel.fullAddress);
    const model: ResidenceAddress = {
      completeAddress: this.turnOffAddressLookup
        ? `${formModel.previousAddress}, ${formModel.previousAddressCity}, ${formModel.previousAddressState} ${formModel.previousAddressZipCode}, ${formModel.previousAddressCountry}`
        : formModel.fullAddress,
      streetAddress: this.turnOffAddressLookup ? formModel.previousAddress : addressModel.streetAddress,
      city: this.turnOffAddressLookup ? formModel.previousAddressCity : addressModel.city,
      province: this.turnOffAddressLookup ? formModel.previousAddressState : addressModel.state,
      country: this.countryLowerCaseCheck(
        this.turnOffAddressLookup ? formModel.previousAddressCountry : addressModel.country
      ),
      postalCode: this.turnOffAddressLookup ? formModel.previousAddressZipCode : addressModel.zipcode,
      livedFromDate: formModel.previousAddressFromDate
        ? (dayjs(formModel.previousAddressFromDate).format('YYYY-MM-DD') as string)
        : '',
      livedToDate: formModel.previousAddressToDate
        ? (dayjs(formModel.previousAddressToDate).format('YYYY-MM-DD') as string)
        : ''
    };
    if (formModel.id && formModel.id != '') {
      model.id = formModel.id;
    }
    return model;
  }

  private prepareSaveModel(): ResidenceAddress {
    const formModel = this.form.value;
    // Construct the complete address by combining relevant fields
    const completeAddress = [
      formModel.streetAddress,
      formModel.city,
      formModel.state,
      formModel.zipCode,
      this.countryLowerCaseCheck(formModel.country)
    ].filter(Boolean)
      .join(', ');      // Join with commas and spaces
    const model: ResidenceAddress = {
      completeAddress: completeAddress,
      streetAddress: formModel.streetAddress,
      city: formModel.city,
      province: formModel.state,
      country: this.countryLowerCaseCheck(formModel.country),
      postalCode: formModel.zipCode,
      livedFromDate: formModel.fromDate ? (dayjs(formModel.fromDate).format('YYYY-MM-DD') as string) : '',
      id: formModel.id ? formModel.id : null
    };
    if (formModel.toDate) {
      model.livedToDate = dayjs(formModel.toDate).format('YYYY-MM-DD') as string;
    }
    if (formModel.isCurrentAddress) {
      model.isCurrentAddress = formModel.isCurrentAddress;
    }
    return model;
  }

  private getPlaceAutocomplete(addressText: any, isPrevious: boolean): void {
    if (!addressText) {
      return;
    }

    const inputElement = addressText?.nativeElement || addressText;
    
    if (!inputElement) {
      return;
    }

    // Clean up existing autocomplete instance if it exists
    const existingInstance = this.autocompleteInstances.get(inputElement.id);
    if (existingInstance) {
      google.maps.event.clearInstanceListeners(inputElement);
      this.autocompleteInstances.delete(inputElement.id);
    }

    try {
      const autocomplete = new google.maps.places.Autocomplete(inputElement, {
        componentRestrictions: {
          country: this.countryUSorCA
        },
        types: [],
        fields: ['formatted_address', 'address_components', 'geometry']
      });

      // Set fields explicitly to reduce payload and improve mobile performance
      autocomplete.setFields(['formatted_address', 'address_components', 'geometry']);

      // Store the autocomplete instance for later use
      this.autocompleteInstances.set(inputElement.id, autocomplete);

      // Add event listener with better error handling
      google.maps.event.addListener(autocomplete, 'place_changed', () => {
        const place = autocomplete.getPlace();
        
        // Check if place has required data
        if (place && place.formatted_address) {
          this.zone.run(() => {
            try {
              if (isPrevious) {
                this.fullAddress.setValue(place.formatted_address);
              } else {
                const addressModel = this.parseAddress(place.formatted_address);
                this.assignAddressField(addressModel);
              }
            } catch (error) {
              console.error('Error processing place selection:', error);
            }
          });
        }
      });

      // Mobile-specific: Ensure autocomplete works well on mobile
      // Note: Removed focus event trigger to prevent Google Maps API errors

    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
    }
  }

  private parseAddress(address: string): AddressModel {
    const parsedAddressArr = address?.split(',');
    const addressModel = new AddressModel();
    if (address && parsedAddressArr.length === 4) {
      addressModel.fullAddress = address;
      addressModel.streetAddress = parsedAddressArr[0]?.trim();
      addressModel.city = parsedAddressArr[1]?.trim();
      addressModel.state = parsedAddressArr[2]?.trim();
      addressModel.zipcode = addressModel.state.substring(3, addressModel.state.length)?.trim();
      addressModel.state = addressModel.state?.split(' ')[0];
      addressModel.country = parsedAddressArr[3]?.trim();
      if (addressModel.country == 'Canada' || addressModel.country == 'CA') {
        addressModel.country = 'CA';
      }
      if (addressModel.country == 'USA' || addressModel.country == 'US') {
        addressModel.country = 'US';
        addressModel.state = `US-${addressModel.state}`;
      }
    }
    return addressModel;
  }
  private createForm(isAddingCurrentAddress?: boolean): UntypedFormGroup {
    const form = this.fb.group({
      completeAddress: ['', Validators.required],
      streetAddress: ['', Validators.required],
      city: ['', [Validators.required, Validators.pattern("^[A-Za-z-'\. ]*$"), Validators.maxLength(25)]],
      state: ['', [Validators.required, Validators.pattern(REGEX_LETTERS_SPACES_HYPHENS), Validators.maxLength(25)]],
      country: ['', Validators.required],
      zipCode: ['', Validators.required],
      // New month/year controls for FROM date
      fromMonth: ['', Validators.required],
      fromYear: ['', Validators.required],
      // New month/year controls for TO date - no validators for current addresses
      toMonth: ['', isAddingCurrentAddress ? [] : Validators.required],
      toYear: ['', isAddingCurrentAddress ? [] : Validators.required],
      // Keep legacy date fields for compatibility (will be populated from month/year)
      toDate: ['', Validators.required],
      fromDate: ['', Validators.required],
      id: [''],
      isCurrentAddress: [!!isAddingCurrentAddress]
    });

    // If this is a current address, set default values for TO date fields
    if (isAddingCurrentAddress) {
      const currentMonth = dayjs(this.currentDate).month();
      const currentYear = dayjs(this.currentDate).year();
      form.patchValue({
        toMonth: currentMonth,
        toYear: currentYear,
        toDate: this.currentDate
      });
    }

    // Auto-compose completeAddress when all parts are present
    const updateCompleteAddress = () => {
      const street = (form.get('streetAddress')?.value || '').toString().trim();
      const city = (form.get('city')?.value || '').toString().trim();
      const state = (form.get('state')?.value || '').toString().trim();
      const zip = (form.get('zipCode')?.value || '').toString().trim();
      const country = (form.get('country')?.value || '').toString().trim();
      if (street && city && state && zip && country) {
        const composed = [street, city, state, zip, this.countryLowerCaseCheck(country)].join(', ');
        if (form.get('completeAddress')?.value !== composed) {
          form.patchValue({ completeAddress: composed }, { emitEvent: false });
        }
      }
    };
    ['streetAddress', 'city', 'state', 'zipCode', 'country'].forEach(ctrl => {
      form.get(ctrl)?.valueChanges.subscribe(updateCompleteAddress);
    });

    // Keep date fields in sync as user selects month/year so form.valid can be true
    form.get('fromMonth')?.valueChanges.subscribe(() => this.updateDateFieldsFromMonthYear());
    form.get('fromYear')?.valueChanges.subscribe(() => this.updateDateFieldsFromMonthYear());
    form.get('toMonth')?.valueChanges.subscribe(() => this.updateDateFieldsFromMonthYear());
    form.get('toYear')?.valueChanges.subscribe(() => this.updateDateFieldsFromMonthYear());

    return form;
  }

  private createPreviousAddressForm(): UntypedFormGroup {
    const form = this.fb.group({
      fullAddress: ['', Validators.required],
      previousAddress: [''],
      previousAddressCity: [''],
      previousAddressState: [''],
      previousAddressCountry: [''],
      previousAddressZipCode: [''],
      // New month/year controls for previous address
      previousFromMonth: ['', Validators.required],
      previousFromYear: ['', Validators.required],
      previousToMonth: ['', Validators.required],
      previousToYear: ['', Validators.required],
      // Keep legacy date fields for compatibility
      previousAddressFromDate: ['', Validators.required],
      previousAddressToDate: ['', Validators.required],
      // Add ID field for editing support
      id: ['']
    });

    // Synchronize legacy date fields when month/year values change
    form.get('previousFromMonth')?.valueChanges.subscribe(() => this.updatePreviousDateFieldsFromMonthYear());
    form.get('previousFromYear')?.valueChanges.subscribe(() => this.updatePreviousDateFieldsFromMonthYear());
    form.get('previousToMonth')?.valueChanges.subscribe(() => this.updatePreviousDateFieldsFromMonthYear());
    form.get('previousToYear')?.valueChanges.subscribe(() => this.updatePreviousDateFieldsFromMonthYear());

    return form;
  }

  // Helper method to convert month/year to date for current address form
  private updateDateFieldsFromMonthYear(): void {
    const fromMonth = this.form.get('fromMonth')?.value;
    const fromYear = this.form.get('fromYear')?.value;
    const toMonth = this.form.get('toMonth')?.value;
    const toYear = this.form.get('toYear')?.value;

    // Update FROM date - use start of month
    if (fromMonth !== null && fromMonth !== '' && fromYear) {
      const fromDate = dayjs().year(fromYear).month(fromMonth).startOf('month');
      this.form.patchValue({ fromDate: fromDate.toDate() }, { emitEvent: false });
    }

    // Update TO date - use end of month to avoid gaps
    if (toMonth !== null && toMonth !== '' && toYear) {
      const toDate = dayjs().year(toYear).month(toMonth).endOf('month');
      this.form.patchValue({ toDate: toDate.toDate() }, { emitEvent: false });
    }
  }

  // Helper method to convert month/year to date for previous address form
  private updatePreviousDateFieldsFromMonthYear(): void {
    const fromMonth = this.previousAddressForm.get('previousFromMonth')?.value;
    const fromYear = this.previousAddressForm.get('previousFromYear')?.value;
    const toMonth = this.previousAddressForm.get('previousToMonth')?.value;
    const toYear = this.previousAddressForm.get('previousToYear')?.value;

    // Update FROM date
    if (fromMonth !== null && fromMonth !== '' && fromYear) {
      const fromDate = dayjs().year(fromYear).month(fromMonth).startOf('month');
      this.previousAddressForm.patchValue({
        previousAddressFromDate: fromDate.format('YYYY-MM-DD')
      }, { emitEvent: false });
    }

    // Update TO date
    if (toMonth !== null && toMonth !== '' && toYear) {
      const toDate = dayjs().year(toYear).month(toMonth).endOf('month');
      this.previousAddressForm.patchValue({
        previousAddressToDate: toDate.format('YYYY-MM-DD')
      }, { emitEvent: false });
    }
  }

  // Helper method to populate month/year dropdowns from existing date
  private populateMonthYearFromDate(address: ResidenceAddress): void {
    if (address.livedFromDate) {
      const fromDate = dayjs(address.livedFromDate);
      this.form.patchValue({
        fromMonth: fromDate.month(),
        fromYear: fromDate.year()
      });
    }

    if (address.isCurrentAddress) {
      // For current addresses, always use current month/year for TO date
      const currentMonth = dayjs(this.currentDate).month();
      const currentYear = dayjs(this.currentDate).year();
      this.form.patchValue({
        toMonth: currentMonth,
        toYear: currentYear
      });
    } else if (address.livedToDate) {
      const toDate = dayjs(address.livedToDate);
      this.form.patchValue({
        toMonth: toDate.month(),
        toYear: toDate.year()
      });
    }
  }

  // Get available months for FROM month dropdown for current address
  getAvailableFromMonths(): any[] {
    const fromYear = this.form.get('fromYear')?.value;
    const editingAddressId = this.form.get('id')?.value;

    if (!fromYear) return this.months;

    if (this.isCurrentAddressUpdateFlow()) {
      const minAllowedFromDate = dayjs(this.minFromDate).startOf('month');
      const maxAllowedFromDate = dayjs(this.currentDate).startOf('month');
      return this.months.filter(month => {
        const candidate = dayjs().year(fromYear).month(month.value).startOf('month');
        return candidate.valueOf() >= minAllowedFromDate.valueOf() && candidate.valueOf() <= maxAllowedFromDate.valueOf();
      });
    }

    // Filter out occupied months
    return this.months.filter(month => {
      return !this.isMonthYearOccupied(month.value, fromYear, editingAddressId);
    });
  }

  // Get available years for FROM year dropdown for current address
  getAvailableFromYears(): number[] {
    const editingAddressId = this.form.get('id')?.value;

    if (this.isCurrentAddressUpdateFlow()) {
      const minYear = dayjs(this.minFromDate).year();
      const maxYear = dayjs(this.currentDate).year();
      return this.years.filter(year => year >= minYear && year <= maxYear);
    }

    // Filter out years that have at least one available month
    return this.years.filter(year => {
      // Check if this year has any available months
      const availableMonthsInYear = this.months.filter(month => {
        return !this.isMonthYearOccupied(month.value, year, editingAddressId);
      });

      return availableMonthsInYear.length > 0;
    });
  }

  // Get available months for TO month dropdown based on FROM date selection for current address
  getAvailableToMonths(): any[] {
    const fromMonth = this.form.get('fromMonth')?.value;
    const fromYear = this.form.get('fromYear')?.value;
    const toYear = this.form.get('toYear')?.value;
    const editingAddressId = this.form.get('id')?.value;
    const isCurrentAddress = this.form.get('isCurrentAddress')?.value;

    if (!toYear) return this.months;

    let availableMonths = this.months;

    // Apply chronological order within this address (FROM <= TO)
    if (fromYear && toYear && fromMonth !== null && fromMonth !== '') {
      // If same year, TO month must be >= FROM month
      if (fromYear === toYear) {
        availableMonths = availableMonths.filter(month => month.value >= fromMonth);
      }
    }

    // For non-current addresses, filter out occupied months
    if (!isCurrentAddress) {
      availableMonths = availableMonths.filter(month => {
        return !this.isMonthYearOccupied(month.value, toYear, editingAddressId);
      });
    }

    return availableMonths;
  }

  // Get available years for TO year dropdown based on FROM year selection for current address
  getAvailableToYears(): number[] {
    const fromYear = this.form.get('fromYear')?.value;
    const editingAddressId = this.form.get('id')?.value;
    const isCurrentAddress = this.form.get('isCurrentAddress')?.value;

    let availableYears = this.years;

    // Apply chronological order within this address (FROM <= TO)
    if (fromYear) {
      availableYears = availableYears.filter(year => year >= fromYear);
    }

    // For non-current addresses, filter out years that have at least one available month for TO date
    if (!isCurrentAddress) {
      availableYears = availableYears.filter(year => {
        // Check if this year has any available months for TO date
        const availableMonthsInYear = this.months.filter(month => {
          // Apply FROM month constraint if same year
          if (fromYear && year === fromYear) {
            const fromMonth = this.form.get('fromMonth')?.value;
            if (fromMonth !== null && fromMonth !== '' && month.value < fromMonth) {
              return false;
            }
          }

          return !this.isMonthYearOccupied(month.value, year, editingAddressId);
        });

        return availableMonthsInYear.length > 0;
      });
    }

    return availableYears;
  }

  // Get available months for previous TO month dropdown 
  getPreviousAvailableToMonths(): any[] {
    const fromMonth = this.previousAddressForm.get('previousFromMonth')?.value;
    const fromYear = this.previousAddressForm.get('previousFromYear')?.value;
    const toYear = this.previousAddressForm.get('previousToYear')?.value;
    const maxAllowedDate = this.getMaxAllowedDateForPreviousAddress();
    const editingAddressId = this.previousAddressForm.get('id')?.value;

    if (!toYear) return this.months;

    let availableMonths = this.months;

    // Apply chronological order within this address (FROM <= TO)
    if (fromYear && toYear && fromMonth !== null && fromMonth !== '') {
      // If same year, TO month must be >= FROM month
      if (fromYear === toYear) {
        availableMonths = availableMonths.filter(month => month.value >= fromMonth);
      }
    }

    // Apply constraint based on maximum allowed date
    if (maxAllowedDate && toYear) {
      // If TO year is the same as max allowed year, limit months
      if (toYear === maxAllowedDate.year()) {
        availableMonths = availableMonths.filter(month => month.value <= maxAllowedDate.month());
      }
      // If TO year is after max allowed year, no months should be available
      else if (toYear > maxAllowedDate.year()) {
        availableMonths = [];
      }
    }

    // Filter out occupied months (but allow the current address range being edited)
    availableMonths = availableMonths.filter(month => {
      return !this.isMonthYearOccupied(month.value, toYear, editingAddressId);
    });

    return availableMonths;
  }

  // Get available months for previous FROM month dropdown - exclude months already covered by existing addresses
  getPreviousAvailableFromMonths(): any[] {
    const fromYear = this.previousAddressForm.get('previousFromYear')?.value;
    const editingAddressId = this.previousAddressForm.get('id')?.value;

    if (!fromYear) return this.months;

    // Filter out occupied months
    return this.months.filter(month => {
      return !this.isMonthYearOccupied(month.value, fromYear, editingAddressId);
    });
  }

  // Get available years for previous TO year dropdown
  getPreviousAvailableToYears(): number[] {
    const fromYear = this.previousAddressForm.get('previousFromYear')?.value;
    const maxAllowedDate = this.getMaxAllowedDateForPreviousAddress();
    const editingAddressId = this.previousAddressForm.get('id')?.value;

    let availableYears = this.years;

    // Limit by FROM year (chronological order within this address)
    if (fromYear) {
      availableYears = availableYears.filter(year => year >= fromYear);
    }

    // Apply minFromDate constraint (for gap filling)
    if (this.minFromDate) {
      const minYear = dayjs(this.minFromDate).year();
      availableYears = availableYears.filter(year => year >= minYear);
    }

    // Apply maxFromDate constraint (for gap filling)
    if (this.maxFromDate) {
      const maxYear = dayjs(this.maxFromDate).year();
      availableYears = availableYears.filter(year => year <= maxYear);
    }

    // Limit by existing addresses (no overlaps)
    if (maxAllowedDate) {
      availableYears = availableYears.filter(year => year <= maxAllowedDate.year());
    }

    // Filter out years that have at least one available month for TO date
    return availableYears.filter(year => {
      // Check if this year has any available months for TO date
      const availableMonthsInYear = this.months.filter(month => {
        // Apply FROM month constraint if same year
        if (fromYear && year === fromYear) {
          const fromMonth = this.previousAddressForm.get('previousFromMonth')?.value;
          if (fromMonth !== null && fromMonth !== '' && month.value < fromMonth) {
            return false;
          }
        }

        // Check against minFromDate/maxFromDate constraints
        if (this.minFromDate) {
          const minDate = dayjs(this.minFromDate);
          if (year < minDate.year() || (year === minDate.year() && month.value < minDate.month())) {
            return false;
          }
        }
        if (this.maxFromDate) {
          const maxDate = dayjs(this.maxFromDate);
          if (year > maxDate.year() || (year === maxDate.year() && month.value > maxDate.month())) {
            return false;
          }
        }

        // Apply max allowed date constraint
        if (maxAllowedDate && year === maxAllowedDate.year() && month.value > maxAllowedDate.month()) {
          return false;
        }

        return !this.isMonthYearOccupied(month.value, year, editingAddressId);
      });

      return availableMonthsInYear.length > 0;
    });
  }

  // Get available years for previous FROM year dropdown - exclude years already fully covered by existing addresses
  getPreviousAvailableFromYears(): number[] {
    const editingAddressId = this.previousAddressForm.get('id')?.value;
    const maxAllowedDate = this.getMaxAllowedDateForPreviousAddress();

    let availableYears = this.years;

    // Apply minFromDate constraint (for gap filling)
    if (this.minFromDate) {
      const minYear = dayjs(this.minFromDate).year();
      availableYears = availableYears.filter(year => year >= minYear);
    }

    // Apply maxFromDate constraint (for gap filling)
    if (this.maxFromDate) {
      const maxYear = dayjs(this.maxFromDate).year();
      availableYears = availableYears.filter(year => year <= maxYear);
    }

    // Filter by maximum allowed date first
    if (maxAllowedDate) {
      availableYears = availableYears.filter(year => year <= maxAllowedDate.year());
    }

    // Filter out years that have at least one available month
    return availableYears.filter(year => {
      // Check if this year has any available months
      const availableMonthsInYear = this.months.filter(month => {
        // Check against minFromDate/maxFromDate constraints
        if (this.minFromDate) {
          const minDate = dayjs(this.minFromDate);
          if (year < minDate.year() || (year === minDate.year() && month.value < minDate.month())) {
            return false;
          }
        }
        if (this.maxFromDate) {
          const maxDate = dayjs(this.maxFromDate);
          if (year > maxDate.year() || (year === maxDate.year() && month.value > maxDate.month())) {
            return false;
          }
        }
        return !this.isMonthYearOccupied(month.value, year, editingAddressId);
      });

      return availableMonthsInYear.length > 0;
    });
  }

  private isCurrentAddressUpdateFlow(): boolean {
    if (!this.isAddingCurrentAddress) {
      return false;
    }
    return this.addresses.some(address => address?.isCurrentAddress && address?.livedFromDate);
  }

  // Helper method to check if a specific month/year is occupied by existing addresses
  private isMonthYearOccupied(month: number, year: number, excludeAddressId?: string): boolean {
    if (!this.addresses?.length) return false;

    return this.addresses.some(addr => {
      // Skip the address being edited
      if (excludeAddressId && addr.id === excludeAddressId) return false;

      if (!addr.livedFromDate) return false;

      const addrFromDate = dayjs(addr.livedFromDate);
      const addrToDate = addr.livedToDate ? dayjs(addr.livedToDate) : dayjs(); // Current date if no end date

      const checkDate = dayjs().year(year).month(month).date(1);

      // Check if the month/year falls within the address period (inclusive)
      // Using basic comparison instead of isSameOrAfter/isSameOrBefore
      const checkTimestamp = checkDate.valueOf();
      const fromTimestamp = addrFromDate.startOf('month').valueOf();
      const toTimestamp = addrToDate.endOf('month').valueOf();

      return checkTimestamp >= fromTimestamp && checkTimestamp <= toTimestamp;
    });
  }

  // Helper method to get the maximum allowed date for a previous address
  private getMaxAllowedDateForPreviousAddress(): dayjs.Dayjs | null {
    if (!this.addresses || this.addresses.length === 0) {
      return null;
    }

    // Get the ID of the address being edited (if any) to exclude it from constraints
    const editingAddressId = this.previousAddressForm.get('id')?.value;

    // Find the current address
    const currentAddress = this.addresses.find(addr => addr.isCurrentAddress === true);

    if (currentAddress && currentAddress.livedFromDate) {
      // Previous address must end in the month before current address starts
      return dayjs(currentAddress.livedFromDate).subtract(1, 'month');
    }

    // If no current address, find the earliest existing address (excluding the one being edited)
    const sortedAddresses = this.addresses
      .filter(addr => addr.livedFromDate && addr.id !== editingAddressId)
      .sort((a, b) => new Date(a.livedFromDate).getTime() - new Date(b.livedFromDate).getTime());

    if (sortedAddresses.length > 0) {
      // Previous address must end in the month before the next address starts
      return dayjs(sortedAddresses[0].livedFromDate).subtract(1, 'month');
    }

    return null;
  }

  // Helper method to get constraint message for user feedback
  getDateConstraintMessage(): string {
    const maxAllowedDate = this.getMaxAllowedDateForPreviousAddress();

    if (maxAllowedDate) {
      const currentAddress = this.addresses.find(addr => addr.isCurrentAddress === true);

      if (currentAddress) {
        const currentFromDate = dayjs(currentAddress.livedFromDate).format('MMMM YYYY');
        const maxAllowedMonth = dayjs(currentAddress.livedFromDate).subtract(1, 'month').format('MMMM YYYY');
        return `Previous addresses must end by ${maxAllowedMonth} (the month before your current address started in ${currentFromDate})`;
      } else {
        return `Previous addresses must not overlap with existing addresses`;
      }
    }

    return '';
  }

  private populateForm(address: ResidenceAddress): void {
    if (address) {
      this.assignAddressField({
        fullAddress: address.completeAddress,
        streetAddress: address.streetAddress,
        city: address.city,
        state: address.province,
        zipcode: address.postalCode,
        country: this.countryUSorCA.includes(address.country.toUpperCase())
          ? address.country.toUpperCase()
          : address.country
      });
      if (address?.livedToDate) {
        this.toDate.setValue(dayjs(address?.livedToDate));
      }
      if (address.livedFromDate) {
        this.fromDate.setValue(dayjs(address?.livedFromDate));
      }
      if (address.isCurrentAddress) {
        this.form.get('isCurrentAddress').setValue(address.isCurrentAddress);
        this.form.get('toDate').setValue(dayjs(this.currentDate));

        // Remove validators from TO date fields for current addresses since they're displayed as text
        this.form.get('toMonth')?.clearValidators();
        this.form.get('toYear')?.clearValidators();
        this.form.get('toMonth')?.updateValueAndValidity();
        this.form.get('toYear')?.updateValueAndValidity();
      }

      // Populate month/year dropdowns from the date values
      this.populateMonthYearFromDate(address);
      this.addressId.setValue(address?.id);
    }
  }

  private assignAddressField(addressModel: AddressModel): void {
    if (!addressModel) {
      return;
    }
    this.completeAddress.setValue(addressModel.fullAddress);
    this.streetAddress.setValue(addressModel.streetAddress);
    this.city.setValue(addressModel.city);
    this.state.setValue(addressModel.state);
    this.country.setValue(addressModel.country);
    this.zipCode.setValue(addressModel.zipcode);
  }

  private resetFlags(): void {
    this.cancelPreviousAddressForm();
    this.onCancelCurrentAddress();
  }

  private checkMissingGap() {
    // Create continuous timeline and calculate coverage
    this.createContinuousTimeline();

    let isYearsInOrder = true;
    let currentDiffAddressMonth = 0;

    // Calculate total coverage from all valid addresses (avoid double counting)
    const validAddresses = this.addresses.filter(addr => addr.livedFromDate);
    for (const address of validAddresses) {
      if (address.livedFromDate) {
        const fromDate = dayjs(address.livedFromDate);
        const toDate = address.livedToDate ? dayjs(address.livedToDate) : dayjs(this.currentDate);
        const monthsDiff = Math.round(toDate.diff(fromDate, 'month', true));
        currentDiffAddressMonth += monthsDiff;
      }
    }

    this.checkAddressHasThreeYearInfo(currentDiffAddressMonth, isYearsInOrder);
    this.updateProgressBar(currentDiffAddressMonth, isYearsInOrder);
  }

  private createContinuousTimeline() {
    // Start from 3 years ago to current date
    const startDate = dayjs(this.currentDate).subtract(3, 'year').startOf('month');
    const endDate = dayjs(this.currentDate).endOf('month');

    // Sort addresses by date (newest first)
    const sortedAddresses = [...this.addresses]
      .filter(addr => addr.livedFromDate)
      .sort((a, b) => dayjs(b.livedFromDate).valueOf() - dayjs(a.livedFromDate).valueOf());

    this.displayAddresses = [];
    let currentTimelineDate = endDate;

    // Process addresses from newest to oldest
    for (let i = 0; i < sortedAddresses.length; i++) {
      const address = sortedAddresses[i];
      const addressFromDate = dayjs(address.livedFromDate).startOf('month');
      const addressToDate = address.livedToDate ?
        dayjs(address.livedToDate).endOf('month') :
        endDate;

      // If there's a gap between current timeline position and this address
      if (currentTimelineDate.isAfter(addressToDate, 'day')) {
        const gapFromDate = addressToDate.add(1, 'month').startOf('month');
        const gapToDate = currentTimelineDate.endOf('month');

        // Only add gap if it's more than a few days (to account for month boundaries)
        if (gapFromDate.isBefore(gapToDate, 'month') || gapFromDate.month() !== gapToDate.month()) {
          this.displayAddresses.push({
            livedFromDate: null,
            expectedFromDate: gapFromDate.format('YYYY-MM-DD'),
            expectedToDate: gapToDate.format('YYYY-MM-DD'),
            isGap: true,
            streetAddress: 'Missing Address Information',
            city: ''
          } as any);
        }
      }

      // Add the actual address
      this.displayAddresses.push(address);

      // Update timeline position to the day before this address started
      currentTimelineDate = addressFromDate.subtract(1, 'day');
    }

    // Check if we need to add a gap at the beginning (going back to 3 years)
    if (currentTimelineDate.isAfter(startDate, 'day')) {
      const gapToDate = currentTimelineDate.endOf('month');

      // Only add if there's a significant gap
      if (startDate.isBefore(gapToDate, 'month')) {
        this.displayAddresses.push({
          livedFromDate: null,
          expectedFromDate: startDate.format('YYYY-MM-DD'),
          expectedToDate: gapToDate.format('YYYY-MM-DD'),
          isGap: true,
          streetAddress: 'Missing Address Information',
          city: ''
        } as any);
      }
    }
  }

  private checkAddressHasThreeYearInfo(month: number, isYearsInOrder: boolean): boolean {
    if (month >= 36 && isYearsInOrder) {
      this.isLessThan3years = false; // Fixed: Should be false when you have 3+ years
      return true;
    }
    this.isLessThan3years = true; // Less than 3 years
    return false;
  }

  private updateProgressBar(currentDiffAddressMonth: number, isYearsInOrder: boolean): void {
    // currentDiffAddressMonth represents MISSING months, so we need to invert it
    // to show COVERED months as the progress
    const missingMonths = currentDiffAddressMonth;
    const coveredMonths = 36 - missingMonths;
    const percent = (coveredMonths / 36) * 100;
    // Progress bar should show how much of the 3-year period is covered
    this.progressBarValue = Math.min(Math.max(percent, 0), 100);
  }

  getMissingDateRange(address: any, index: number): string {
    if (address?.livedFromDate) {
      return ''; // Not a missing data entry
    }

    // For gap entries created by the timeline
    if (address?.isGap && address?.expectedFromDate && address?.expectedToDate) {
      const fromDate = dayjs(address.expectedFromDate);
      const toDate = dayjs(address.expectedToDate);
      
      // Validate dates before formatting
      if (fromDate.isValid() && toDate.isValid()) {
        return `${fromDate.format('MMM YYYY')} - ${toDate.format('MMM YYYY')}`;
      }
    }

    // Legacy fallback for old gap detection
    // Find the previous and next addresses with dates
    let prevAddress: ResidenceAddress | null = null;
    let nextAddress: ResidenceAddress | null = null;

    // Look for previous address with date
    for (let i = index - 1; i >= 0; i--) {
      if (this.displayAddresses[i]?.livedFromDate) {
        prevAddress = this.displayAddresses[i];
        break;
      }
    }

    // Look for next address with date
    for (let i = index + 1; i < this.displayAddresses.length; i++) {
      if (this.displayAddresses[i]?.livedFromDate) {
        nextAddress = this.displayAddresses[i];
        break;
      }
    }

    // Calculate missing date range
    let fromDate: string = '';
    let toDate: string = '';

    if (nextAddress?.livedToDate) {
      const nextToDate = dayjs(nextAddress.livedToDate);
      if (nextToDate.isValid()) {
        fromDate = nextToDate.add(1, 'month').format('MMM YYYY');
      }
    } else if (index === 0) {
      // This is the earliest missing entry, calculate from 3 years ago
      const startDate = dayjs(this.lastDate);
      if (startDate.isValid()) {
        fromDate = startDate.format('MMM YYYY');
      } else {
        // Fallback: calculate 3 years ago from current date
        const threeYearsAgo = dayjs().subtract(3, 'year');
        fromDate = threeYearsAgo.format('MMM YYYY');
      }
    }

    if (prevAddress?.livedFromDate) {
      const prevFromDate = dayjs(prevAddress.livedFromDate);
      if (prevFromDate.isValid()) {
        toDate = prevFromDate.subtract(1, 'month').format('MMM YYYY');
      }
    } else if (nextAddress?.isCurrentAddress && nextAddress?.livedFromDate) {
      const nextFromDate = dayjs(nextAddress.livedFromDate);
      if (nextFromDate.isValid()) {
        toDate = nextFromDate.subtract(1, 'month').format('MMM YYYY');
      }
    }

    if (fromDate && toDate) {
      return `${fromDate} - ${toDate}`;
    } else if (fromDate) {
      return `From ${fromDate}`;
    } else if (toDate) {
      return `Until ${toDate}`;
    }

    // Fallback with actual dates
    const currentYear = new Date().getFullYear();
    const threeYearsAgo = currentYear - 3;
    return `Sep ${threeYearsAgo} - Sep ${currentYear}`;
  }

  private loadGoogleAutoComplete(index: number): void {
    if (this.isGoogleAddressLookupEnabled) {
      // Use requestAnimationFrame to ensure DOM is ready, especially on mobile
      requestAnimationFrame(() => {
        const dynamicCurrentAddressId = document.querySelector('#addressText' + index) as HTMLInputElement;
        
        if (dynamicCurrentAddressId) {
          // Additional check to ensure element is fully rendered
          if (dynamicCurrentAddressId.offsetParent !== null || dynamicCurrentAddressId.offsetWidth > 0) {
            this.getPlaceAutocomplete(dynamicCurrentAddressId, false);
          } else {
            // Element not visible yet, try again after a short delay
            setTimeout(() => {
              const retryElement = document.querySelector('#addressText' + index) as HTMLInputElement;
              
              if (retryElement) {
                this.getPlaceAutocomplete(retryElement, false);
              }
            }, 150);
          }
        }
      });
    }
  }

  private checkIsYearsInOrder(currentDiffAddressMonth) {
    if (currentDiffAddressMonth < 36) {
      return false;
    } else {
      return true;
    }
  }

  onCountryChange(formName, field): void {
    this[formName].patchValue({
      [field]: ''
    });
  }

  onCertnCountrySearch(event: any): void {
    const searchText = event?.target?.value?.trim();

    let countries = this.filterCountryDropdown();
    if (searchText) {
      this.countries = countries.filter(country => country.key.toLowerCase().includes(searchText.toLowerCase()));
    } else {
      this.countries = countries;
    }
  }

  filterCountryDropdown() {
    const isCurrentAddressFlow = this.isAddingCurrentAddress && this.isCurrentAddressEdit;

    // Keep Google autocomplete constrained to CA/US, but allow all countries
    // whenever manual entry is selected.
    if (isCurrentAddressFlow && this.isGoogleAddressLookupEnabled) {
      return this.certnCountries.filter(country => this.countryUSorCA.includes(country.value));
    }

    return this.certnCountries;
  }

  // Utility method to set/clear validators and update validity
  private updateFormControlValidators(control: any, validators: any[] | null) {
    if (validators) {
      control.setValidators(validators);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }

  onTurnOffAddressAutoLookup() {
    this.turnOffAddressLookup = true;
    this.countries = this.filterCountryDropdown();
    
    // Configure validators for manual address entry
    const manualValidators = [
      Validators.required,
      Validators.pattern(REGEX_LETTERS_SPACES_HYPHENS),
      Validators.maxLength(25)
    ];
    
    this.updateFormControlValidators(this.fullAddress, null);
    this.updateFormControlValidators(this.previousAddress, [Validators.required]);
    this.updateFormControlValidators(this.previousAddressCity, manualValidators);
    this.updateFormControlValidators(this.previousAddressState, manualValidators);
    this.updateFormControlValidators(this.previousAddressCountry, [Validators.required]);
    this.updateFormControlValidators(this.previousAddressZipCode, [Validators.required]);
    
    this.previousAddressForm.patchValue({ fullAddress: '' });
  }

  onTurnOnAddressAutoLookup() {
    this.turnOffAddressLookup = false;
    this.countries = this.filterCountryDropdown();
    
    // Configure validators for auto address lookup
    this.updateFormControlValidators(this.fullAddress, [Validators.required]);
    this.updateFormControlValidators(this.previousAddress, null);
    this.updateFormControlValidators(this.previousAddressCity, null);
    this.updateFormControlValidators(this.previousAddressState, null);
    this.updateFormControlValidators(this.previousAddressCountry, null);
    this.updateFormControlValidators(this.previousAddressZipCode, null);
    
    this.previousAddressForm.patchValue({
      previousAddress: '',
      previousAddressCity: '',
      previousAddressState: '',
      previousAddressCountry: '',
      previousAddressZipCode: ''
    });

    // Reinitialize Google autocomplete after the view updates
    setTimeout(() => {
      const dynamicPreviousAddressId = document.querySelector('#previousAddressText' + this.currentIndex) as HTMLInputElement;
      if (dynamicPreviousAddressId) {
        this.getPlaceAutocomplete(dynamicPreviousAddressId, true);
      }
    }, 100);
  }

  onPreviousAddressLookupCheckboxToggle(event: any): void {
    if (event?.checked) {
      this.onTurnOffAddressAutoLookup();
      return;
    }
    this.onTurnOnAddressAutoLookup();
  }

  resetPreviousAddressValidation() {
    this.updateFormControlValidators(this.fullAddress, [Validators.required]);
    this.updateFormControlValidators(this.previousAddress, null);
    this.updateFormControlValidators(this.previousAddressCity, null);
    this.updateFormControlValidators(this.previousAddressState, null);
    this.updateFormControlValidators(this.previousAddressCountry, null);
    this.updateFormControlValidators(this.previousAddressZipCode, null);
  }

  onGoogleAddressLookupToggle(event: any, index: number): void {
    this.isGoogleAddressLookupEnabled = !this.isGoogleAddressLookupEnabled;
    this.countries = this.filterCountryDropdown();

    this.form.get('streetAddress')?.setValue('');
    this.form.get('country')?.setValue('');
    this.form.get('state')?.setValue('');
    this.form.get('city')?.setValue('');
    this.form.get('zipCode')?.setValue('');

    setTimeout(() => {
      if (this.isGoogleAddressLookupEnabled) {
        const input = document.querySelector('#addressText' + index) as HTMLInputElement;
        
        if (input) {
          this.loadGoogleAutoComplete(index);
        } else {
          console.warn('Google address input field not found after toggle.');
        }
      } else {
        const input = document.querySelector('#addressText' + index) as HTMLInputElement;
        if (input) {
          this.form.get('completeAddress').clearValidators();
          this.form.get('completeAddress').updateValueAndValidity();
          const autocompleteInstance = this.autocompleteInstances.get(input.id);
          if (autocompleteInstance) {
            google.maps.event.clearInstanceListeners(input);
            this.autocompleteInstances.delete(input.id);
          }
        }
      }
    }, 0);
  }

  canDeleteAddress(address: ResidenceAddress, index: number): boolean {
    // Don't show delete button if:
    // 1. Address doesn't have required data (livedFromDate) or is a gap
    // 2. There's only one address left
    // 3. It's the current address (current addresses should NOT have delete option)
    if (!address?.livedFromDate || (address as any)?.isGap) {
      return false;
    }

    if (this.addresses?.length <= 1) {
      return false;
    }

    // Current address should NEVER have a delete button
    // All other (previous) addresses should have delete buttons
    if (address?.isCurrentAddress) {
      return false;
    }

    // All previous addresses can be deleted
    return true;
  }

  deleteAddress(address: ResidenceAddress, index: number): void {
    if (!address?.id) {
      return;
    }

    const addressText = `${address.streetAddress}, ${address.city}`;
    const confirmMessage = `Are you sure you want to delete this address?\n\n${addressText}`;

    if (confirm(confirmMessage)) {
      this.isContentReady = false;
      this.registrationService
        .deleteAddress(address.id)
        .pipe(
          catchError((error: any) => {
            this.isContentReady = true;
            this.toastService.showError(error?.message || 'Failed to delete address');
            return of(false);
          })
        )
        .subscribe(res => {
          if (res) {
            this.toastService.showSuccess('Address deleted successfully');
            this.loadAllAddresses(); // Reload the addresses list
          }
        });
    }
  }
}

